// The game's live link from the browser (protocol 1, delve-core livelink.md): ws://127.0.0.1:<port>/livelink?token=<t>,
// one JSON request per text frame {id, cmd, args: [strings], json}, one reply {id, ok, error, result}. The game
// checks the page's Origin and the token (printed in its console and written to <pack data>/admin/livelink.json);
// a refusal is a plain HTTP answer, so the browser only sees a failed connection. Ported from the desktop Workshop's
// livelink.py, typed helper per command.

export const PROTOCOL = 1;
export const DEFAULT_PORT = 47800;

export class LiveLinkError extends Error {}

/** `hello`: the game, its pack, the Unity version, the scene, and whether it runs in the Editor. */
export interface Hello {
  protocol: number; game?: string; pack?: string; packVersion?: string; unity?: string; scene?: string; editor?: boolean;
  [key: string]: unknown;
}

export interface SocketLike {
  readyState: number;
  send(data: string): void;
  close(): void;
  onopen: (() => void) | null;
  onclose: ((e: { code: number; reason: string }) => void) | null;
  onerror: (() => void) | null;
  onmessage: ((e: { data: unknown }) => void) | null;
}

export type SocketFactory = (url: string) => SocketLike;

const browserSocket: SocketFactory = (url) => new WebSocket(url) as unknown as SocketLike;

/** Whether the game lets a page of `origin` connect without --admin-origin= (LiveLinkServer.Admit): localhost and
 * 127.0.0.1 on any port over http, and the deployed workshop. */
export function originAllowedByDefault(origin: string): boolean {
  const o = origin.toLowerCase();
  return /^http:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/.test(o) || o === "https://delve-workshop.pages.dev";
}

export const linkUrl = (port: number, token: string): string =>
  `ws://127.0.0.1:${port}/livelink?token=${encodeURIComponent(token.trim())}`;

interface Pending { resolve(text: string): void; reject(e: Error): void; timer: ReturnType<typeof setTimeout> }

export class LiveLinkClient {
  private socket: SocketLike | null = null;
  private nextId = 1;
  private pending = new Map<number, Pending>();
  private closeListeners = new Set<(reason: string) => void>();

  constructor(private readonly factory: SocketFactory = browserSocket, readonly timeoutMs = 60_000) {}

  get connected(): boolean {
    return this.socket !== null && this.socket.readyState === 1;
  }

  /** Opens the link and returns the game's hello (game, pack, protocol, scene). */
  async connect(port: number, token: string): Promise<Hello> {
    this.close();
    const socket = this.factory(linkUrl(port, token));
    this.socket = socket;
    await new Promise<void>((resolve, reject) => {
      socket.onopen = () => resolve();
      socket.onerror = () => reject(new LiveLinkError(
        `No game answered on port ${port}. Start the game with --admin, check the port, then paste the token from its console or admin/livelink.json (a wrong token or an origin the game does not allow is refused the same way).`,
      ));
    });
    socket.onmessage = ({ data }) => this.receive(data);
    socket.onerror = null;
    socket.onclose = ({ reason }) => {
      this.socket = null;
      this.failAll(new LiveLinkError("The game closed the live link."));
      for (const listener of this.closeListeners) listener(reason || "closed");
    };
    const hello = JSON.parse(await this.call("hello")) as Hello;
    if (hello.protocol !== PROTOCOL) {
      this.close();
      throw new LiveLinkError(`The game speaks live link protocol ${hello.protocol}; the workshop needs ${PROTOCOL}.`);
    }
    return hello;
  }

  /** Called when the game ends the link (not when `close` does); returns the unsubscribe. */
  onClosed(listener: (reason: string) => void): () => void {
    this.closeListeners.add(listener);
    return () => this.closeListeners.delete(listener);
  }

  close(): void {
    const socket = this.socket;
    this.socket = null;
    if (socket) {
      socket.onclose = null;
      socket.close();
    }
    this.failAll(new LiveLinkError("Not connected to the game."));
  }

  private failAll(e: Error): void {
    for (const p of this.pending.values()) {
      clearTimeout(p.timer);
      p.reject(e);
    }
    this.pending.clear();
  }

  private receive(data: unknown): void {
    let reply: { id?: number; ok?: boolean; error?: string; result?: string };
    try {
      reply = JSON.parse(String(data)) as typeof reply;
    } catch {
      return;
    }
    // id -1: the game could not read the request's id; it answers the oldest one.
    const id = reply.id === -1 ? this.pending.keys().next().value : reply.id;
    const p = id === undefined ? undefined : this.pending.get(id);
    if (!p || id === undefined) return;
    this.pending.delete(id);
    clearTimeout(p.timer);
    if (reply.ok) p.resolve(reply.result ?? "");
    else p.reject(new LiveLinkError(reply.error || "unknown error"));
  }

  /** One command; resolves with its result text, rejects with the game's message. */
  call(cmd: string, args: (string | number | boolean)[] = [], json?: unknown): Promise<string> {
    const socket = this.socket;
    if (!socket || socket.readyState !== 1) return Promise.reject(new LiveLinkError("Not connected to the game."));
    const id = this.nextId++;
    return new Promise<string>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(id);
        reject(new LiveLinkError(`The game did not answer ${cmd} in ${this.timeoutMs / 1000} s.`));
      }, this.timeoutMs);
      this.pending.set(id, { resolve, reject, timer });
      socket.send(JSON.stringify({
        id, cmd, args: args.map(String), json: json === undefined ? null : typeof json === "string" ? json : JSON.stringify(json),
      }));
    });
  }

  private async json<T>(cmd: string, args: (string | number | boolean)[] = [], payload?: unknown): Promise<T> {
    const text = await this.call(cmd, args, payload);
    try {
      return (text ? JSON.parse(text) : {}) as T;
    } catch {
      return { text } as T;
    }
  }

  // -- tuning
  schema = () => this.json<{ version: number; tunables: unknown[] }>("schema");
  get = (id: string) => this.call("get", [id]);
  set = (id: string, value: string) => this.call("set", [id, value]);
  reset = (id?: string) => this.call("reset", id ? [id] : []);
  async changed(): Promise<Record<string, string>> {
    const out: Record<string, string> = {};
    for (const line of (await this.call("changed")).split("\n")) {
      const at = line.indexOf(" = ");
      if (at > 0) out[line.slice(0, at)] = line.slice(at + 3);
    }
    return out;
  }
  profileExport = (name = "Workshop profile") =>
    this.json<{ name: string; description?: string; values: { id: string; value: string }[]; commands?: string[] }>("profile.export", [name]);
  /** Applies a tuning profile; answers the ids it could not apply. */
  async profileApply(profile: unknown): Promise<string[]> {
    return (await this.call("profile.apply", [], profile)).split("\n").filter((l) => l.trim());
  }
  console = (line: string) => this.call("console", [line]);

  // -- floors and benches
  /** Dungeon scene only: rebuilds the current floor; answers what was built. */
  floorRebuild = (seed: number, depth: number, biome?: string) =>
    this.json<{ seed: number; depth: number; biome: string }>("floor.rebuild", [seed, depth, ...(biome ? [biome] : [])]);
  biomePlay = (userBiome: unknown, seed?: number, depth?: number) =>
    this.call("biome.play", [seed, depth].filter((a): a is number => a !== undefined), userBiome);
  metricsStart = () => this.call("metrics.start");
  /** Writes metrics_*.csv and answers the folder (on the game's machine); fails with "not recording". */
  metricsStop = () => this.call("metrics.stop");
  /** "started", or "busy: <status>" (still ok) when a bench already runs. */
  benchFloors = (biomes: string, depths: string, seeds: number, difficulty = "Medium", timeout = 300) =>
    this.call("bench.floors", [biomes, depths, seeds, difficulty, timeout]);
  benchHorde = (counts = "50,100,150") => this.call("bench.horde", [counts]);
  /** `report`: the last report's path on the game's machine ("" before the first). */
  benchStatus = () => this.json<BenchStatus>("bench.status");

  // -- play: who plays, which run, powers, a live picture
  party = () => this.json<Party>("party");
  playStart = (spec: PlaySpec) => this.call("play.start", [], spec);
  heroCast = (player: number, slot: number) => this.call("hero.cast", [player, slot]);
  heroDemo = (classId: string, slot = 0, monster?: string) => this.call("hero.demo", [classId, slot, ...(monster ? [monster] : [])]);
  heroDemoStop = () => this.call("hero.demo", ["stop"]);
  /** A small JPEG of the game view, as a data URL. */
  async viewFrame(width = 640, ui = true): Promise<string> {
    return `data:image/jpeg;base64,${await this.call("view.frame", [width, ...(ui ? [] : ["ui=false"])])}`;
  }

  // -- arena: team A against team B
  arenaOpen = (scene = "EnemyArena") => this.call("arena.open", [scene]);
  /** "started"; a refused fight (busy, unknown ids, empty team) fails with the reasons. */
  arenaStart = (spec: ArenaSpec) => this.call("arena.start", [], spec);
  arenaStatus = () => this.json<ArenaStatus>("arena.status");
  arenaStop = () => this.call("arena.stop");

  // -- monsters' modular appearance
  /** One inert monster in front of player 1: `seed` "" or null = the signature look, `gore` 0..1 (negative = the
   * player's setting), `turn` spins it. */
  monsterPreview = (contentId: string, seed: string | number | null = null, gore = -1, turn = false) =>
    this.json<LookInfo>("monster.preview", [], { contentId, seed: seed === null ? "" : String(seed), gore, turn });
  monsterPreviewStop = () => this.call("monster.preview", ["stop"]);
  /** The looks the game rolls for seeds seed..seed+n-1 (n is clamped to 1..64 by the game). */
  monsterRoll = (contentId: string, seed: string | number, n = 8) =>
    this.json<{ contentId: string; set: string; looks: LookInfo[] }>("monster.roll", [], { contentId, seed: String(seed), n });
  hordePreview = (fodder = 100, regular = 15) => this.json<{ fodder: number; regular: number }>("horde.preview", [fodder, regular]);
}

export interface Named { id: string; name: string }

/** A hero in the game now (`party`): player 1-4, its class, a bot or the person at the game. */
export interface PartyMember { player: number; classId: string; className: string; bot: boolean; skill: string; alive: boolean }

/** `party` (PlayTools.Describe): the scene, who plays, and what play.start can pick (the Run Catalog's order first). */
export interface Party {
  scene?: string; party?: PartyMember[]; classes?: Named[]; difficulties?: Named[]; biomes?: Named[];
}

export type BotSkill = "Rookie" | "Normal" | "Veteran";

/** One seat of a run: at most one is played by the person at the game, the others become bots. */
export interface PlaySeat { classId: string; bot: boolean; skill?: BotSkill | string }

/** What play.start takes (PlaySpec in the game): empty biome / difficulty = the Run Catalog's first / Medium, seed 0
 * = random, arenaRule Survival or Trial. */
export interface PlaySpec {
  mode: "Dungeon" | "Arena";
  party: PlaySeat[];
  biomeId?: string; difficultyId?: string; seed?: number; arenaRule?: "Survival" | "Trial" | string;
}

/** One entry of an arena team (ArenaFighter): a hero class or a monster (content id, asset or display name). */
export interface ArenaFighter { id: string; count: number; player: boolean; elites: string[] }

/** arena.start's fight (ArenaSpec, schema 1). */
export interface ArenaSpec {
  schema: 1; teamA: ArenaFighter[]; teamB: ArenaFighter[];
  /** Difficulty id or name, "" = the arena's current one. */
  difficulty: string;
  rounds: number; timeout: number; separation: number; timeScale: number; depth: number;
}

/** What one fighter did in a round (diedAt -1 = survived). */
export interface ArenaFighterResult {
  team: "A" | "B" | string; kind: "hero" | "monster" | string; id: string; name: string; player: boolean; alive: boolean;
  damageDealt: number; damageTaken: number; healthLeft: number; maxHealth: number; kills: number; diedAt: number;
}

export interface ArenaRound { round: number; winner: "A" | "B" | "draw" | string; seconds: number; fighters: ArenaFighterResult[] }

/** arena.status (ArenaReport): the rounds finished so far of the current series (cleared by each arena.start). */
export interface ArenaStatus { busy: boolean; status: string; errors: string[]; rounds: ArenaRound[] }

export interface BenchStatus { busy: boolean; status: string; report: string }

/** A modular monster's look (MonsterPreview.LookInfo): its seed, its description and the visible part meshes. */
export interface LookInfo { seed: string; look: string; parts: string[] }
